import { UserType } from '@/types/modelEnums';
import type { User } from '@/types/models';

export type NavbarVariant = 'default' | 'transparent' | 'dark';

export const NAVBAR_BASE_CLASS = 'p-3 fixed left-0 right-0 z-50 px-0 sm:px-6';

export function getNavbarContainerClass(variant: NavbarVariant): string {
  if (variant === 'transparent') return 'bg-transparent';
  if (variant === 'dark') return 'bg-primary/90 backdrop-blur-[50px]';
  return 'bg-white/90 backdrop-blur-[50px] !backdrop-grayscale-[0.5] border-b border-primary';
}

export function getNavbarTextClass(variant: NavbarVariant): string {
  if (variant === 'dark') return '!text-white';
  return 'text-primary';
}

export function isNavbarHrefActive(pathname: string, href: string): boolean {
  if (href.split('/').filter(Boolean).length === 1) return pathname === href;
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * Maps an authenticated user to its role dashboard route for the marketing
 * navbar's "Go to Dashboard" CTA.
 *
 * The /admin route gate (middleware) keys on `custom:role === 'admin'`, NOT on
 * `custom:type`, so admin is resolved from `user.role` (which the hook populates
 * from custom:role). Everyone else is keyed on `user.type`. Case-insensitive;
 * PlatformOwner has no dedicated dashboard and intentionally falls back to
 * /patient, as do unknown/missing types.
 */
export function getDashboardPathForUser(user: User | null | undefined): string {
  if ((user?.role || '').toLowerCase() === 'admin') return '/admin';

  switch ((user?.type || '').toLowerCase()) {
    case UserType.PROVIDER.toLowerCase():
      return '/provider';
    case UserType.SPONSOR.toLowerCase():
      return '/sponsor';
    case UserType.ADMIN.toLowerCase():
      // type='Admin' without role='admin' would be bounced by the route gate;
      // route to /admin anyway so the CTA matches the user's declared type.
      return '/admin';
    case UserType.PATIENT.toLowerCase():
    default:
      // Includes UserType.PLATFORM_OWNER (no dedicated route) and unknown types.
      return '/patient';
  }
}

export function getNavbarAuthPath(pathname: string): string {
  if (pathname.startsWith('/users/auth/patient')) return '/users/auth/patient';
  if (pathname.startsWith('/users/auth/provider')) return '/users/auth/provider';
  if (pathname.startsWith('/patient/') || pathname.startsWith('/about/patients')) {
    return '/users/auth/patient';
  }
  if (pathname.startsWith('/provider/') || pathname.startsWith('/about/providers')) {
    return '/users/auth/provider';
  }
  return '/users/auth/provider';
}
